import {
  DeleteOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Space, Table, Modal, Spin, Button } from 'antd';
import Search from 'antd/lib/input/Search';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { GroupAuthority } from '../../enums/GroupAuthority';
import { deleteKapsules, getKapsules } from '../../services/KapsuleService';
import { Kapsule } from '../../types/Kapsule';
import { KapsuleFieldsTable } from '../kapsuleFieldsTable/KapsuleFieldsTable';

type KapsulesTableProps = {};

const openKapsuleInfoModal = (record: Kapsule) => {
  Modal.info({
    title: record.name,
    maskClosable: true,
    content: <KapsuleFieldsTable fields={record.fields} />,
    onOk() {},
  });
};

const kapsulesSearchTextFilter = (
  record: Kapsule,
  searchText: string
): boolean => {
  if (searchText === '') return true;
  return (
    record.name.toLowerCase().includes(searchText) ||
    record.table.toLowerCase().includes(searchText)
  );
};

export const KapsulesTable: FunctionComponent<KapsulesTableProps> = () => {
  const [searchText, setSearchText] = useState<string>('');
  const { url } = useRouteMatch();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: Kapsule, b: Kapsule) => a.name.localeCompare(b.name),
    },
    {
      title: 'ID',
      dataIndex: '_id',
      sorter: (a: Kapsule, b: Kapsule) => a._id.localeCompare(b._id),
    },
    {
      title: 'Binding Komment Table',
      dataIndex: 'table',
      sorter: (a: Kapsule, b: Kapsule) => a.table.localeCompare(b.table),
    },
    {
      title: 'Key Field',
      dataIndex: 'keyField',
      sorter: (a: Kapsule, b: Kapsule) => a.keyField.localeCompare(b.keyField),
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      sorter: (a: Kapsule, b: Kapsule) =>
        (a.owner || '').localeCompare(b.owner),
    },
    {
      title: 'Last Update',
      dataIndex: 'updatedAt',
      sorter: (a: Kapsule, b: Kapsule) => {
        const aTime = new Date(a.updatedAt).getTime();
        const bTime = new Date(b.updatedAt).getTime();
        return aTime - bTime;
      },
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (_text: string, record: Kapsule) => (
        <Space size="middle">
          <Link to={`${url}/${record._id}`}>
            <FolderOpenOutlined />
          </Link>
          <InfoCircleOutlined
            style={{ color: '#1890ff' }}
            onClick={() => openKapsuleInfoModal(record)}
          />
          {record.authority?.includes(GroupAuthority.DELETE) ? (
            <DeleteOutlined
              style={{ color: '#1890ff' }}
              onClick={() => {
                Modal.confirm({
                  title: 'Confirm',
                  content: `Are you sure to delete ${record.name}?`,
                  okText: 'Yes',
                  cancelText: 'Cancel',
                  onOk: async () => {
                    await deleteKapsules([record._id]);
                    reloadKapsules();
                  },
                });
              }}
            />
          ) : null}
        </Space>
      ),
    },
  ];

  const [kapsules, setKapsules] = useState<Kapsule[]>();
  const [selectedKapsuleIds, setSelectedKapsuleIds] = useState<string[]>([]);
  useEffect(() => {
    reloadKapsules();
  }, []);

  const onSearch = (searchText: string) => {
    if (searchText) {
      setSearchText(searchText.toLowerCase());
    } else {
      setSearchText('');
    }
  };

  const reloadKapsules = async () => {
    try {
      const kapsules = await getKapsules();
      setKapsules(kapsules);
    } catch (e) {
      setKapsules([]);
    }
  };

  const rowSelection = {
    onChange: (_selectedRowKeys: React.Key[], selectedRows: Kapsule[]) => {
      const selectedIds = selectedRows.map((item) => item._id);
      setSelectedKapsuleIds(selectedIds);
    },
  };

  return kapsules ? (
    <Table
      title={() => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <b>Kapsules</b>
          <Space>
            <Button
              disabled={selectedKapsuleIds.length === 0}
              type="primary"
              onClick={() => {
                Modal.confirm({
                  title: 'Confirm',
                  content: `Are you sure to delete selected Kapsules?`,
                  okText: 'Yes',
                  cancelText: 'Cancel',
                  onOk: async () => {
                    await deleteKapsules(selectedKapsuleIds);
                    setSelectedKapsuleIds([]);
                    reloadKapsules();
                  },
                });
              }}
            >
              Delete Selection
            </Button>
            <Search
              placeholder="input search text"
              allowClear
              onSearch={onSearch}
              style={{ width: 200 }}
            />
          </Space>
        </div>
      )}
      rowKey={(record) => record._id}
      columns={columns}
      dataSource={kapsules.filter((item) =>
        kapsulesSearchTextFilter(item, searchText)
      )}
      rowSelection={{
        type: 'checkbox',
        ...rowSelection,
      }}
    />
  ) : (
    <div
      style={{
        height: 312,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spin />
    </div>
  );
};
